import React from "react";
import { MessageSquare, Cpu, Lightbulb, Award } from "lucide-react";
import { motion } from "framer-motion";
import { CambridgeSummerConfig, CambridgeSummerHighlight } from "../../../data/cambridgeSummerConfig";

interface ProgramFeaturesProps {
    config: CambridgeSummerConfig;
}

const iconMap: Record<string, React.ElementType> = {
    MessageSquare,
    Cpu,
    Lightbulb,
    Award,
};

const ProgramFeatures: React.FC<ProgramFeaturesProps> = ({ config }) => {
    const { highlights } = config;

    // Cambridge Blue & Teal alternating
    const gradients = [
        "from-[#003C71] to-[#0066CC]",
        "from-[#2C5F6F] to-[#3D7A8A]",
        "from-[#2C5F6F] to-[#3D7A8A]",
        "from-[#003C71] to-[#0066CC]",
    ];
    const accentColors = [
        "#7BB4E7",
        "#5BA3B5",
        "#5BA3B5",
        "#7BB4E7",
    ];
    const textColors = [
        "text-white",
        "text-white",
        "text-white",
        "text-white",
    ];

    return (
        <section className="py-8 md:py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-8"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-4xl font-bold text-[#111827] mb-4">
                        Program Highlights
                    </h2>
                    <p className="text-lg md:text-xl text-[#4B5563] max-w-xl mx-auto">
                        Master academic English, AI literacy, entrepreneurship, and innovation in a Cambridge environment
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {highlights.map((highlight: CambridgeSummerHighlight, index: number) => {
                        const IconComponent = iconMap[highlight.icon] || Award;
                        const textColor = textColors[index];
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div
                                    className={`relative overflow-hidden rounded-3xl shadow-lg bg-gradient-to-br ${gradients[index]} p-8 h-full`}
                                >
                                    <div
                                        className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl opacity-50"
                                        style={{ backgroundColor: accentColors[index] }}
                                    />
                                    <div
                                        className="relative w-14 h-14 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6"
                                        style={{ backgroundColor: accentColors[index] }}
                                    >
                                        <IconComponent className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className={`relative text-2xl font-bold ${textColor} mb-4`}>
                                        {highlight.title}
                                    </h3>
                                    <p className={`relative ${textColor} opacity-90 leading-relaxed text-base`}>
                                        {highlight.description}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default ProgramFeatures;
